<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-xl bg-gray-800 text-gray-300 rounded-lg shadow-lg border border-gray-700 animate-scaleIn flex flex-col gap-4 relative" style="max-height:90vh;">
      <!-- Header -->
      <div class="flex items-center justify-between px-4 pt-4">
        <div class="flex items-center gap-3">
          <img
            :src="equipmentData?.img || defaultImg"
            alt="Equipment Image"
            class="w-9 h-9 sm:w-14 sm:h-14 rounded-md object-cover border-2"
            :class="equipmentData?.rarityColors?.borderColor || 'border-gray-600'"
            @error="handleImageError"
          />
          <div>
            <h1 class="text-base sm:text-lg font-semibold" :class="equipmentData?.rarityColors?.textColor || 'text-gray-300'">
              {{ equipmentData?.name || 'ไม่ระบุ' }}
            </h1>
            <p class="text-gray-300 text-xs sm:text-sm">ประเภท: {{ equipmentData?.type || 'ไม่ระบุ' }}</p>
            <div v-if="equipmentData?.rarity" class="flex items-center gap-1 mt-1">
              <template v-for="star in Number(equipmentData.rarity)" :key="star">
                <i class="fas fa-star text-yellow-400 text-xs sm:text-sm"></i>
              </template>
            </div>
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 group"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>

      <!-- Notification -->
      <div v-if="notificationMessage" class="px-4 -mt-2">
        <div class="text-center text-xs sm:text-sm" :class="notificationType === 'success' ? 'text-green-400' : 'text-red-300'">
          {{ notificationMessage }}
        </div>
      </div>

      <!-- Content -->
      <div class="px-4 pb-4 space-y-4 overflow-y-auto scrollbar-thin" style="max-height: calc(90vh - 170px);">
        <!-- Installed Cards Selection -->
        <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
          <h2 class="text-sm font-semibold text-gray-100 mb-2">เลือกการ์ดที่ต้องการถอน</h2>
          <div v-if="installedCards.length === 0" class="text-center text-yellow-400 text-xs sm:text-sm">
            ไม่พบการ์ดที่ติดตั้งในอวัยวะจักรกลนี้
          </div>
          <div v-else class="space-y-2 max-h-32 overflow-y-auto scrollbar-thin">
            <div
              v-for="card in installedCards"
              :key="card.id"
              @click="selectCard(card.id)"
              class="flex items-center gap-2 bg-gray-700 p-2 rounded-md cursor-pointer hover:bg-gray-600 transition-colors"
              :class="{ 'border-indigo-500 bg-indigo-900': selectedCardId === card.id }"
            >
              <img
                :src="card.img || defaultImg"
                class="w-6 h-6 rounded object-cover border"
                :class="card.rarityColors?.borderColor || 'border-gray-600'"
                @error="handleImageError"
              />
              <span class="text-gray-300 flex-1">
                {{ card.name }} <span v-if="card.level">+{{ card.level }}</span>
              </span>
              <input
                type="checkbox"
                :checked="selectedCardId === card.id"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-600 rounded pointer-events-none"
              />
            </div>
          </div>
        </section>

        <!-- Stats Preview (Negative Changes) -->
        <section v-if="selectedCard && activeStats.length > 0" class="bg-gray-800 border border-gray-700 rounded-md p-3">
          <h2 class="text-sm font-semibold text-gray-100 mb-2">สถานะที่จะลดลงหลังถอน</h2>
          <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
            <div v-for="{ key, value } in activeStats" :key="key" class="flex items-center gap-2">
              <i :class="['text-indigo-400 text-sm', statIcons[key] || 'fas fa-star']"></i>
              <span class="text-gray-300 capitalize">
                {{ key }}: <strong class="text-red-400">-{{ value }}</strong>
              </span>
            </div>
          </div>
        </section>

        <!-- Cost Info -->
        <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
          <p class="text-sm text-gray-100 mb-2">ค่าใช้จ่ายในการถอน: <strong class="text-yellow-400">100 เพชร</strong></p>
          <p class="text-xs text-gray-400">การ์ดจะถูกคืนสู่กระเป๋า และเพชรจะถูกหักจากบัญชีของคุณ</p>
        </section>

        <!-- Action Button -->
        <section>
          <button
            v-if="!isUninstalling"
            @click="uninstallCard"
            class="w-full bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm py-2 rounded-md transition-colors duration-200 border border-red-700 flex items-center justify-center gap-2"
            :disabled="!selectedCardId"
          >
            <i class="fas fa-id-card text-xs"></i>
            <span>ถอนการติดตั้งการ์ด</span>
          </button>
          <div v-else class="flex items-center justify-center py-2">
            <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-red-600"></div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { projectFunctions } from '@/firebase/config';
import gameItems from '@/data/gameItems.json';
import defaultImg from '@/assets/default-item.png';

export default {
  name: 'UninstallCardModal',
  props: {
    itemId: {
      type: String,
      required: true,
    },
    inventoryItems: {
      type: Array,
      required: true,
    },
  },
  emits: ['close', 'card-uninstalled'],
  setup(props, { emit }) {
    const equipmentData = ref(null);
    const installedCards = ref([]);
    const selectedCardId = ref(null);
    const isUninstalling = ref(false);
    const notificationMessage = ref('');
    const notificationType = ref('success');

    const statIcons = {
      stamina: 'fas fa-heart',
      str: 'fas fa-dumbbell',
      int: 'fas fa-brain',
      agi: 'fas fa-running',
      luck: 'fas fa-dice',
      maxHp: 'fas fa-heartbeat',
      atk: 'fas fa-fist-raised',
      evasion: 'fas fa-running',
      critical: 'fas fa-bolt',
      defense: 'fas fa-shield-alt',
    };

    const statKeys = [
      'stamina',
      'str',
      'int',
      'agi',
      'luck',
      'maxHp',
      'atk',
      'evasion',
      'critical',
      'defense',
    ];

    // Computed property to filter non-zero stats (negative for preview)
    const activeStats = computed(() => {
      const selected = installedCards.value.find(card => card.id === selectedCardId.value);
      if (!selected || !selected.stats) return [];
      return statKeys
        .map(key => ({
          key,
          value: selected.stats[key] || 0,
        }))
        .filter(stat => stat.value > 0);
    });

    const selectedCard = computed(() => {
      return installedCards.value.find(card => card.id === selectedCardId.value);
    });

    const selectCard = (id) => {
      selectedCardId.value = selectedCardId.value === id ? null : id;
    };

    const handleImageError = (event) => {
      event.target.src = defaultImg;
    };

    const showNotification = (message, type = 'success') => {
      notificationMessage.value = message;
      notificationType.value = type;
      setTimeout(() => {
        notificationMessage.value = '';
      }, 3000);
    };

    const uninstallCard = async () => {
      if (!selectedCardId.value) {
        showNotification('กรุณาเลือกการ์ดที่ต้องการถอน', 'error');
        return;
      }
      isUninstalling.value = true;
      try {
        const selected = selectedCard.value;
        const uninstallCardFunction = projectFunctions.httpsCallable('gameActions');
        const result = await uninstallCardFunction({
          action: 'mechanicalOrganAction',
          subAction: 'uninstallCard',
          cardId: selected.id,
          equipmentId: props.itemId,
          slotIndex: selected.slotIndex,
        });
        const { success, message, updatedItem } = result.data;
        if (success) {
          showNotification('ถอนการติดตั้งการ์ดสำเร็จ!', 'success');
          emit('card-uninstalled', updatedItem);
          emit('close');
        } else {
          showNotification(message || 'ถอนการติดตั้งการ์ดล้มเหลว', 'error');
        }
      } catch (err) {
        showNotification(err.message || 'เกิดข้อผิดพลาดในการถอนการติดตั้งการ์ด', 'error');
      } finally {
        isUninstalling.value = false;
      }
    };

    const syncEquipmentData = () => {
      const found = props.inventoryItems.find(item => item.id === props.itemId || item.instanceId === props.itemId);
      if (!found) {
        showNotification('ไม่พบข้อมูลอวัยวะจักรกล', 'error');
        emit('close');
        return;
      }
      equipmentData.value = found;
      const slots = found.cardSlots || found.states?.cardSlots || [];
      installedCards.value = slots.map((slot, index) => {
        const template = gameItems[slot.cardTemplateId] || gameItems[slot.cardName] || {};
        return {
          id: slot.cardInstanceId || slot.cardTemplateId || `slot-${index}`,
          slotIndex: Number.isInteger(slot.slotIndex) ? slot.slotIndex : index,
          templateId: slot.cardTemplateId,
          name: slot.cardName || template.name || slot.cardTemplateId,
          img: slot.img || template.img,
          rarity: slot.rarity ?? template.rarity ?? 1,
          rarityColors: slot.rarityColors ?? template.rarityColors,
          stats: slot.cardStats || template.stats || {},
        };
      });
    };

    onMounted(syncEquipmentData);
    watch(() => props.inventoryItems, syncEquipmentData, { deep: true });

    return {
      equipmentData,
      installedCards,
      selectedCardId,
      isUninstalling,
      notificationMessage,
      notificationType,
      statIcons,
      statKeys,
      activeStats,
      selectedCard,
      defaultImg,
      selectCard,
      handleImageError,
      showNotification,
      uninstallCard,
    };
  },
};
</script>

<style scoped>
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

button:not(:disabled):active {
  transform: scale(0.98);
}
</style>
